import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/Buttons/Button';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StyleSheet, View, Pressable, ActivityIndicator, Animated, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { accountApi } from '@/lib/api';
import { useToastStore } from '@/store';
import { useEarnProgramsRefreshStore } from '@/store/earnProgramsRefresh';
import Svg, { Circle } from 'react-native-svg';

const EARN_META = {
  daily_claim:             { icon: 'calendar-check', label: 'Daily Claim',         hint: 'Come back every day' },
  import_completion:       { icon: 'file-import',    label: 'Import Chats',        hint: 'Bring your Gab AI, ChatGPT, Claude, or Grok history' },
  first_voice_session:     { icon: 'microphone',     label: 'First Voice Session',  hint: 'Have a hands-free conversation' },
  first_memory:            { icon: 'brain',          label: 'First Memory',         hint: 'Teach the AI something about you' },
  first_file_upload:       { icon: 'file-arrow-up',  label: 'First File Upload',    hint: 'Upload a PDF, image, or document' },
  tried_3_models:          { icon: 'layer-group',    label: 'Model Explorer',       hint: 'Try 3 different AI models' },
  first_image_gen:         { icon: 'image',          label: 'First Image',          hint: 'Generate your first image' },
  win_back:                { icon: 'rotate-left',    label: 'Welcome Back',         hint: 'Return after time away' },
  share_chat:              { icon: 'share-nodes',    label: 'Share a Chat',         hint: 'Make a conversation public' },
  email_verified:          { icon: 'envelope-circle-check', label: 'Verify Email',  hint: 'Confirm your email address' },
  profile_complete:        { icon: 'user-check',     label: 'Complete Profile',     hint: 'Add your name and role' },
  enable_2fa:              { icon: 'shield-halved',  label: 'Enable 2FA',           hint: 'Secure your account' },
  pinned_conversation_first: { icon: 'thumbtack',    label: 'Pin a Chat',           hint: 'Pin your first conversation' },
};

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

function ProgressRing({ progress, size = 72, strokeWidth = 5, color = '#10B981' }) {
  const animValue = useRef(new Animated.Value(0)).current;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    Animated.timing(animValue, {
      toValue: progress,
      duration: 800,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [progress]);

  const strokeDashoffset = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: [circumference, 0],
  });

  return (
    <Svg width={size} height={size}>
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(128,128,128,0.12)"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <AnimatedCircle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        rotation="-90"
        origin={`${size / 2}, ${size / 2}`}
      />
    </Svg>
  );
}

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },

  heroCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    padding: 18,
    borderRadius: 14,
    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.06)' : 'rgba(16, 185, 129, 0.05)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)',
  },
  heroRingContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroRingLabel: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTextBlock: {
    flex: 1,
    gap: 4,
  },
  heroCreditsPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    marginTop: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.14)' : 'rgba(16, 185, 129, 0.10)',
  },

  dailyCard: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  dailyCardInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
  },
  dailyCardAvailable: {
    backgroundColor: '#10B981',
  },
  dailyCardClaimed: {
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    borderRadius: 12,
  },
  dailyLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  dailyIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },

  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
    marginBottom: 2,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sectionCount: {
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },

  card: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    gap: 12,
  },
  cardCompleted: {
    borderColor: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)',
    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.04)' : 'rgba(16, 185, 129, 0.03)',
  },
  cardClaimable: {
    borderColor: isDark ? 'rgba(245, 158, 11, 0.35)' : 'rgba(245, 158, 11, 0.3)',
    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.06)' : 'rgba(245, 158, 11, 0.04)',
  },

  cardCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardCheckboxPending: {
    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
  },
  cardCheckboxDone: {
    backgroundColor: '#10B981',
    borderColor: '#10B981',
  },

  cardIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardBody: {
    flex: 1,
    gap: 1,
  },
  cardRight: {
    alignItems: 'flex-end',
    gap: 2,
  },
  creditChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
  },
  creditChipDone: {
    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.08)',
  },
  claimButton: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#F59E0B',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 60,
  },
  recurringHint: {
    marginTop: 1,
  },

  footer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 15,
    padding: 15,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
});

function EarnCreditsSettings() {
  const { t } = useTranslation();
  const [loadError, setLoadError] = useState(false);
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { addToast } = useToastStore(state => state.actions);
  const programs = useEarnProgramsRefreshStore((s) => s.programs);
  const fetchEarnPrograms = useEarnProgramsRefreshStore((s) => s.fetchEarnPrograms);

  const [loading, setLoading] = useState(true);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const [claimingKey, setClaimingKey] = useState(null);

  const loadPrograms = useCallback(async ({ force } = {}) => {
    try {
      setLoading(true);
      setLoadError(false);
      await fetchEarnPrograms({ force });
    } catch (err) {
      setLoadError(true);
      console.error('Error loading earn programs:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchEarnPrograms]);

  useEffect(() => { loadPrograms(); }, [loadPrograms]);

  const handleDailyClaim = useCallback(async () => {
    try {
      setClaimingDaily(true);
      await accountApi.claimDailyCredits();
      await loadPrograms({ force: true });
    } catch (err) {
      addToast(err?.message || 'Could not claim daily credits', 'error');
    } finally {
      setClaimingDaily(false);
    }
  }, [addToast, loadPrograms]);

  const handleClaim = useCallback(async (programKey) => {
    try {
      setClaimingKey(programKey);
      await accountApi.claimEarnProgram(programKey);
      await loadPrograms({ force: true });
    } catch (err) {
      addToast(err?.message || 'Could not claim credits', 'error');
    } finally {
      setClaimingKey(null);
    }
  }, [addToast, loadPrograms]);

  const isRecurring = (p) => p.caps?.daily || p.caps?.weekly || p.caps?.monthly;

  const oneTimePrograms = useMemo(() =>
    programs.filter(p => p.key !== 'daily_claim' && p.key !== 'win_back' && !isRecurring(p)),
    [programs],
  );

  const availableOneTime = useMemo(() => oneTimePrograms.filter(p => p.isAvailable), [oneTimePrograms]);
  const completedOneTime = useMemo(() => oneTimePrograms.filter(p => !p.isAvailable), [oneTimePrograms]);
  const sortedOneTime = useMemo(() => [...availableOneTime, ...completedOneTime], [availableOneTime, completedOneTime]);

  const recurringPrograms = useMemo(() =>
    programs.filter(p => p.key !== 'daily_claim' && p.key !== 'win_back' && isRecurring(p)),
    [programs],
  );

  const totalOneTime = oneTimePrograms.length;
  const completedCount = completedOneTime.length;
  const progress = totalOneTime > 0 ? completedCount / totalOneTime : 0;
  const totalEarnable = useMemo(() =>
    programs.filter(p => p.isAvailable).reduce((sum, p) => sum + p.credits, 0),
    [programs],
  );

  const dailyProgram = programs.find(p => p.key === 'daily_claim');
  const dailyAvailable = dailyProgram?.isAvailable;

  if (loadError && !loading) return <View style={{ gap: 16, paddingVertical: 24 }}>
    <Text accessibilityRole="alert" color="danger">{t('settingsPage.earnLoadFailed')}</Text>
    <Button text={t('common.retry')} onPress={() => loadPrograms({ force: true })} />
  </View>;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator color={colors.text_color_secondary} />
      </View>
    );
  }

  const getRecurringLabel = (p) => {
    const parts = [];
    if (p.caps?.daily) parts.push(`${p.caps.daily}× daily`);
    if (p.caps?.weekly) parts.push(`${p.caps.weekly}× weekly`);
    if (p.caps?.monthly) parts.push(`${p.caps.monthly}× monthly`);
    return parts.join(' · ') || 'Repeatable';
  };

  const renderCard = (program) => {
    const meta = EARN_META[program.key] || { icon: 'gift', label: program.description, hint: '' };
    const recurring = isRecurring(program);
    const claimable = program.isClaimable && program.isAvailable;
    const isClaiming = claimingKey === program.key;

    const lifetimeCap = program.caps?.lifetime;
    const completedGrants = program.completedGrants ?? 0;
    const hasMultiLifetime = typeof lifetimeCap === 'number' && lifetimeCap > 1;
    const fullyExhausted = !program.isAvailable;
    const partialMultiProgress = !recurring && hasMultiLifetime && completedGrants > 0 && !fullyExhausted;
    const completed = recurring
      ? !program.isAvailable
      : (hasMultiLifetime ? fullyExhausted : !program.isAvailable);
    const showSuccessCheck = !recurring && (completed || partialMultiProgress);
    const strikeTitle = !recurring && completed;

    const secondaryHint = claimable
      ? 'You already qualify — tap to claim!'
      : partialMultiProgress
        ? `Earned ${completedGrants} of ${lifetimeCap} platform bonuses — use another device for more`
        : meta.hint;

    const cardContent = (
      <View style={[styles.card, showSuccessCheck && !recurring && styles.cardCompleted, claimable && styles.cardClaimable]}>
        <View style={[
          styles.cardCheckbox,
          showSuccessCheck && !recurring ? styles.cardCheckboxDone : styles.cardCheckboxPending,
        ]}>
          {showSuccessCheck && !recurring && <Icon name="check" size={11} color="#fff" />}
        </View>

        <View style={[
          styles.cardIconWrap,
          { backgroundColor: showSuccessCheck && !recurring
              ? (isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.08)')
              : claimable
                ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)')
                : (isDark ? `${colors.text_color_primary}15` : `${colors.solid_color_tertiary}`),
          },
        ]}>
          <Icon
            name={meta.icon}
            size={15}
            color={showSuccessCheck && !recurring ? '#10B981' : claimable ? '#F59E0B' : colors.text_color_primary}
          />
        </View>

        <View style={styles.cardBody}>
          <Text
            size="s"
            weight={strikeTitle ? 'regular' : 'medium'}
            color="primary"
            style={strikeTitle ? { textDecorationLine: 'line-through', opacity: 0.6 } : undefined}
          >
            {meta.label}
          </Text>
          <Text size="xs" color="secondary" numberOfLines={2}>
            {secondaryHint}
          </Text>
          {recurring && (
            <Text size="xxs" color="secondary" style={styles.recurringHint}>{getRecurringLabel(program)}</Text>
          )}
        </View>

        <View style={styles.cardRight}>
          {claimable ? (
            <View style={styles.claimButton}>
              {isClaiming
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text size="xs" weight="bold" style={{ color: '#fff' }}>Claim</Text>
              }
            </View>
          ) : (
            <>
              <View style={[styles.creditChip, showSuccessCheck && !recurring && styles.creditChipDone]}>
                <Text
                  size="xs"
                  weight="bold"
                  style={{ color: showSuccessCheck && !recurring ? 'rgba(16,185,129,0.5)' : '#10B981' }}
                >
                  +{program.credits}
                </Text>
              </View>
              {showSuccessCheck && !recurring && (
                <Text size="xxs" style={{ color: '#10B981' }}>
                  {partialMultiProgress ? `${completedGrants}/${lifetimeCap}` : 'Earned'}
                </Text>
              )}
            </>
          )}
        </View>
      </View>
    );

    if (claimable) {
      return (
        <Pressable
          key={program.key}
          onPress={() => handleClaim(program.key)}
          disabled={isClaiming}
          style={({ pressed }) => [pressed && { opacity: 0.85 }]}
        >
          {cardContent}
        </Pressable>
      );
    }

    return <View key={program.key}>{cardContent}</View>;
  };

  return (
    <View style={styles.container}>
      {/* Hero progress card */}
      <View style={styles.heroCard}>
        <View style={styles.heroRingContainer}>
          <ProgressRing progress={progress} size={72} strokeWidth={5} />
          <View style={styles.heroRingLabel}>
            <Text size="l" weight="bold" color="primary">{completedCount}</Text>
            <Text size="xxs" color="secondary">/ {totalOneTime}</Text>
          </View>
        </View>
        <View style={styles.heroTextBlock}>
          <Text size="l" weight="bold" color="primary">
            {completedCount === totalOneTime ? 'All Earned!' : 'Earn Free Credits'}
          </Text>
          <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>
            {completedCount === totalOneTime
              ? 'You\'ve completed every challenge. Nice work.'
              : 'Complete challenges to unlock free credits.'}
          </Text>
          {totalEarnable > 0 && (
            <View style={styles.heroCreditsPill}>
              <Icon name="coins" size={11} color="#10B981" />
              <Text size="xs" weight="bold" style={{ color: '#10B981' }}>{totalEarnable} credits available</Text>
            </View>
          )}
        </View>
      </View>

      {/* Daily claim */}
      {dailyProgram && (
        <Pressable
          onPress={dailyAvailable ? handleDailyClaim : undefined}
          disabled={!dailyAvailable || claimingDaily}
          style={[
            styles.dailyCard,
            dailyAvailable ? styles.dailyCardAvailable : styles.dailyCardClaimed,
          ]}
        >
          <View style={styles.dailyCardInner}>
            <View style={styles.dailyLeft}>
              <View style={[styles.dailyIconWrap, {
                backgroundColor: dailyAvailable ? 'rgba(255,255,255,0.2)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
              }]}>
                {claimingDaily
                  ? <ActivityIndicator size="small" color={dailyAvailable ? '#fff' : colors.text_color_secondary} />
                  : <Icon name="calendar-check" size={15} color={dailyAvailable ? '#fff' : colors.text_color_secondary} />
                }
              </View>
              <View style={{ gap: 1 }}>
                <Text size="s" weight="semibold" style={{ color: dailyAvailable ? '#fff' : colors.text_color_primary }}>
                  {dailyAvailable ? 'Claim Daily Credits' : 'Daily Credits Claimed'}
                </Text>
                <Text size="xs" style={{ color: dailyAvailable ? 'rgba(255,255,255,0.7)' : colors.text_color_secondary }}>
                  {dailyAvailable ? 'Tap to collect your free credits' : 'Come back tomorrow for more'}
                </Text>
              </View>
            </View>
            <View style={[styles.creditChip, { backgroundColor: dailyAvailable ? 'rgba(255,255,255,0.2)' : 'rgba(16,185,129,0.12)' }]}>
              <Text size="xs" weight="bold" style={{ color: dailyAvailable ? '#fff' : '#10B981' }}>
                +{dailyProgram.credits}
              </Text>
            </View>
          </View>
        </Pressable>
      )}

      {/* One-time challenges */}
      {sortedOneTime.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionHeaderLeft}>
              <Icon name="trophy" size={13} color={colors.text_color_primary} />
              <Text size="s" weight="semibold" color="primary">One-Time Rewards</Text>
            </View>
            <View style={styles.sectionCount}>
              <Text size="xxs" weight="semibold" style={{ color: '#10B981' }}>
                {completedCount}/{totalOneTime}
              </Text>
            </View>
          </View>
          {sortedOneTime.map(renderCard)}
        </View>
      )}

      {/* Recurring programs */}
      {recurringPrograms.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionHeaderLeft}>
              <Icon name="arrows-rotate" size={13} color={colors.text_color_primary} />
              <Text size="s" weight="semibold" color="primary">Recurring</Text>
            </View>
          </View>
          {recurringPrograms.map(renderCard)}
        </View>
      )}

      {/* Footer info */}
      <View style={styles.footer}>
        <Icon name="circle-info" size={12} color={colors.text_color_secondary} style={{ marginTop: 2 }} />
        <Text size="xs" color="secondary" style={{ flex: 1, lineHeight: 18 }}>
          Credits are granted automatically when you complete an action. If you already qualify, tap the Claim button to collect.
        </Text>
      </View>
    </View>
  );
}

export default EarnCreditsSettings;
